/**
 * Reportes comparativos (S9): el estado de resultados contra el período anterior,
 * el mismo del año anterior y el acumulado; por centro de costo (una finca con sus
 * lotes); el balance comparativo; y el envío programado por correo.
 *
 * Cada columna es el mismo Estado de resultados o Balance general de siempre para
 * su rango: el servidor solo los pone lado a lado.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Download, FileText, ChevronRight, Send, Trash2, Plus, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import api from '../../utils/api';
import useAuthStore from '../../store/authStore';
import usePeriodosContables from '../../hooks/usePeriodosContables';
import { ordenarPeriodos, periodoQueCubre, hoyCR, diaDe } from '../../utils/periodosContables';

const num = (n) => { const v = Number(n) || 0; if (Math.abs(v) < 0.005) return '—'; const t = Math.abs(v).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); return v < 0 ? `(${t})` : t; };
const pct = (p) => (p == null ? '' : `${p > 0 ? '+' : ''}${p.toFixed(1)}%`);
const TABS = [['resultados', 'Estado de resultados comparativo'], ['dimension', 'Por centro de costo'], ['balance', 'Balance comparativo'], ['programados', 'Envío por correo']];

const bajar = async (url, nombre) => {
  const r = await api.get(url, { responseType: 'blob' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = nombre; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

export default function ReportesComparativos() {
  const { currentEmisor } = useAuthStore();
  const periodos = usePeriodosContables();
  const lista = useMemo(() => ordenarPeriodos(periodos.filter(p => p.tipo !== 'anual')), [periodos]);
  const [tab, setTab] = useState('resultados');
  const [filtro, setFiltro] = useState({ modo: 'periodo', periodoId: '', desde: '', hasta: '', fecha: hoyCR() });
  // Por omisión, el último período terminado.
  useEffect(() => {
    if (!lista.length || filtro.periodoId) return;
    const actual = periodoQueCubre(lista, hoyCR());
    const i = actual ? lista.findIndex(p => p.id === actual.id) : lista.length;
    const p = lista[Math.max(0, i - 1)] || lista[lista.length - 1];
    setFiltro(f => ({ ...f, periodoId: String(p.id), desde: diaDe(p.fechaInicio), hasta: diaDe(p.fechaFin) }));
  }, [lista, filtro.periodoId]);
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (filtro.modo === 'periodo' && filtro.periodoId) p.set('periodoId', filtro.periodoId);
    else if (tab === 'balance') p.set('fecha', filtro.fecha);
    else { p.set('desde', filtro.desde); p.set('hasta', filtro.hasta); }
    return p;
  }, [filtro, tab]);
  if (!currentEmisor) return null;
  return (
    <div className="space-y-4" data-prueba="comparativos">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Reportes comparativos</h1>
        <p className="text-sm text-gray-500">Cada columna es el estado de siempre para su período, puesto al lado de los otros.</p>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={clsx('px-3 py-2 text-sm border-b-2 -mb-px', tab === k ? 'border-indigo-500 text-indigo-700 font-semibold' : 'border-transparent text-gray-500')}>{l}</button>)}
      </div>
      {tab !== 'programados' && <Filtro filtro={filtro} setFiltro={setFiltro} lista={lista} balance={tab === 'balance'} />}
      {tab === 'resultados' && <Comparativo tipo="resultados" qs={qs} />}
      {tab === 'dimension' && <PorDimension qs={qs} />}
      {tab === 'balance' && <Comparativo tipo="balance" qs={qs} />}
      {tab === 'programados' && <Programados />}
    </div>
  );
}

function Filtro({ filtro, setFiltro, lista, balance }) {
  return (
    <div className="bg-white rounded-xl border p-3 flex flex-wrap items-end gap-3 text-sm" data-prueba="filtro-comparativo">
      <div className="flex gap-1 text-xs">{[['periodo', 'Período contable'], ['fechas', balance ? 'Fecha de corte' : 'Fechas']].map(([k, l]) => <button key={k} onClick={() => setFiltro({ ...filtro, modo: k })} className={clsx('px-2 py-1 rounded border', filtro.modo === k ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-200')}>{l}</button>)}</div>
      {filtro.modo === 'periodo' ? (
        <label className="text-xs text-gray-600">Período<select name="periodoId" value={filtro.periodoId} onChange={(e) => setFiltro({ ...filtro, periodoId: e.target.value })} className="block border rounded-lg px-2 py-1.5 mt-1 text-sm">{[...lista].reverse().map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></label>
      ) : balance ? (
        <label className="text-xs text-gray-600">Al<input type="date" name="fecha" value={filtro.fecha} onChange={(e) => setFiltro({ ...filtro, fecha: e.target.value })} className="block border rounded-lg px-2 py-1.5 mt-1" /></label>
      ) : (<>
        <label className="text-xs text-gray-600">Desde<input type="date" name="desde" value={filtro.desde} onChange={(e) => setFiltro({ ...filtro, desde: e.target.value })} className="block border rounded-lg px-2 py-1.5 mt-1" /></label>
        <label className="text-xs text-gray-600">Hasta<input type="date" name="hasta" value={filtro.hasta} onChange={(e) => setFiltro({ ...filtro, hasta: e.target.value })} className="block border rounded-lg px-2 py-1.5 mt-1" /></label>
      </>)}
    </div>
  );
}

function Descargas({ ruta, qs, base }) {
  return (
    <div className="flex gap-2 text-sm">
      <button onClick={() => bajar(`/contabilidad/comparativos/${ruta}/excel?${qs}`, `${base}.xlsx`).catch(() => {})} className="inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg hover:bg-gray-50"><Download size={14} /> Excel</button>
      <button onClick={() => bajar(`/contabilidad/comparativos/${ruta}/pdf?${qs}`, `${base}.pdf`).catch(() => {})} className="inline-flex items-center gap-1 px-3 py-1.5 border rounded-lg hover:bg-gray-50"><FileText size={14} /> PDF</button>
    </div>
  );
}

/** La matriz: secciones con sus cuentas, totales y, si viene, la variación. */
function Matriz({ datos, conVariacion, onColumna }) {
  return (
    <div className="bg-white rounded-xl border overflow-x-auto">
      <table className="w-full text-sm" data-prueba="matriz">
        <thead className="text-[10.5px] uppercase text-gray-500 border-b">
          <tr>
            <th className="text-left px-4 py-2 min-w-[240px] sticky left-0 bg-white z-10">Cuenta</th>
            {datos.columnas.map(c => (
              <th key={c.clave} className="text-right px-3 py-2 whitespace-nowrap" data-columna={c.clave}>
                {onColumna && c.tieneHijos ? <button onClick={() => onColumna(c)} className="inline-flex items-center gap-0.5 text-indigo-600 uppercase" title="Ver sus centros">{c.nombre}<ChevronRight size={12} /></button> : c.nombre}
                {c.desde && <div className="normal-case font-normal text-[10px] text-gray-400">{c.desde} a {c.hasta}</div>}
              </th>
            ))}
            {conVariacion && <><th className="text-right px-3">Var. vs {datos.columnas[1].clave === 'anterior' ? 'anterior' : 'mes ant.'}</th><th className="text-right px-3">Var. vs año anterior</th></>}
          </tr>
        </thead>
        <tbody>
          {datos.secciones.map(s => (
            <Fragment key={s.clave}>
              {!s.resultado && <tr className="bg-gray-50"><td className="px-4 py-1.5 text-xs font-semibold text-gray-600 uppercase sticky left-0 bg-gray-50">{s.titulo}</td><td colSpan={datos.columnas.length + (conVariacion ? 2 : 0)} /></tr>}
              {(s.cuentas || []).map(c => (
                <tr key={`${s.clave}-${c.id}`} className="border-t border-gray-100">
                  <td className="px-4 py-1.5 pl-6 text-gray-700 sticky left-0 bg-white"><span className="font-mono text-xs text-gray-400 mr-2">{c.codigo}</span>{c.nombre}</td>
                  {c.valores.map((v, i) => <td key={i} className="px-3 text-right font-mono text-[13px] whitespace-nowrap">{num(v)}</td>)}
                  {conVariacion && c.variaciones && [c.variaciones.anterior, c.variaciones.anioAnterior].map((x, i) => <td key={i} className="px-3 text-right font-mono text-[12px] text-gray-600 whitespace-nowrap">{num(x.diferencia)} <span className="text-gray-400">{pct(x.porcentaje)}</span></td>)}
                </tr>
              ))}
              <tr className={clsx('border-t', s.resultado ? 'bg-indigo-50/40 border-indigo-100' : 'border-gray-200')} data-total={s.clave}>
                <td className={clsx('px-4 py-2 font-semibold text-gray-900 sticky left-0', s.resultado ? 'bg-indigo-50' : 'bg-white')}>{s.resultado ? s.titulo : `Total ${s.titulo.toLowerCase()}`}</td>
                {s.totales.map((v, i) => <td key={i} className="px-3 text-right font-mono font-semibold whitespace-nowrap">{num(v)}</td>)}
                {conVariacion && s.variaciones && [s.variaciones.anterior, s.variaciones.anioAnterior].map((x, i) => <td key={i} className="px-3 text-right font-mono text-[12px] font-semibold whitespace-nowrap">{num(x.diferencia)} <span className="text-gray-400 font-normal">{pct(x.porcentaje)}</span></td>)}
              </tr>
            </Fragment>
          ))}
          {(datos.indicadores || []).map(u => (
            <Fragment key={`ind-${u.id}`}>
              <tr className="bg-gray-50" data-indicador={u.simbolo}><td className="px-4 py-1.5 text-xs font-semibold text-gray-600 uppercase sticky left-0 bg-gray-50">Indicadores por {u.simbolo}</td><td colSpan={datos.columnas.length + (conVariacion ? 2 : 0)} /></tr>
              {[[`${u.nombre}${u.tipo === 'saldo' ? ' (último dato)' : ''}`, u.unidades, true], [`Costo por ${u.simbolo}`, u.costoPor], [`Ingreso por ${u.simbolo}`, u.ingresoPor], [`Utilidad por ${u.simbolo}`, u.utilidadPor]].map(([nombre, vals, cantidad]) => (
                <tr key={nombre} className="border-t border-gray-100">
                  <td className="px-4 py-1.5 pl-6 text-gray-700 sticky left-0 bg-white">{nombre}</td>
                  {vals.map((v, i) => <td key={i} className="px-3 text-right font-mono text-[13px] whitespace-nowrap">{v == null ? <span className="text-gray-300 font-sans text-xs">sin dato</span> : cantidad ? Number(v).toLocaleString('es-CR', { maximumFractionDigits: u.decimales }) : num(v)}</td>)}
                  {conVariacion && <><td /><td /></>}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Comparativo({ tipo, qs }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!qs.get('periodoId') && !qs.get('desde') && !qs.get('fecha')) return;
    setDatos(null); setError(null);
    api.get(`/contabilidad/comparativos/${tipo}?${qs}`).then(r => setDatos(r.data.data)).catch(e => setError(e.response?.data?.error || e.message));
  }, [tipo, qs]);
  if (error) return <div className="bg-white rounded-xl border p-6 text-sm text-gray-500">{error}</div>;
  if (!datos) return <div className="bg-white rounded-xl border p-10 text-center text-gray-400 text-sm">Cargando…</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center">
        <div className="mr-auto text-xs text-gray-500">{tipo === 'balance' ? (datos.cuadra.every(Boolean) ? 'Los tres cortes cuadran: activo = pasivo + patrimonio.' : 'Algún corte no cuadra: revise el balance de esa fecha.') : 'Los porcentajes comparan contra el valor absoluto del período con que se compara.'}</div>
        <Descargas ruta={tipo} qs={qs} base={tipo === 'balance' ? 'balance-comparativo' : 'resultados-comparativo'} />
      </div>
      <Matriz datos={datos} conVariacion />
    </div>
  );
}

function PorDimension({ qs }) {
  const [padre, setPadre] = useState(null); // { id, nombre }
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const consulta = useMemo(() => { const p = new URLSearchParams(qs); if (padre) p.set('padreId', padre.id); return p; }, [qs, padre]);
  useEffect(() => {
    if (!qs.get('periodoId') && !qs.get('desde')) return;
    setDatos(null); setError(null);
    api.get(`/contabilidad/comparativos/dimension?${consulta}`).then(r => setDatos(r.data.data)).catch(e => setError(e.response?.data?.error || e.message));
  }, [consulta, qs]);
  if (error) return <div className="bg-white rounded-xl border p-6 text-sm text-gray-500">{error}</div>;
  if (!datos) return <div className="bg-white rounded-xl border p-10 text-center text-gray-400 text-sm">Cargando…</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {padre && <button onClick={() => setPadre(null)} className="text-sm text-gray-500 inline-flex items-center gap-1"><ArrowLeft size={14} /> Todos los centros</button>}
        <div className="mr-auto text-xs text-gray-500">{datos.rango.nombre}{padre ? ` · dentro de ${padre.nombre}` : ''} · {datos.cuadra ? 'las columnas suman el total' : 'las columnas no suman el total: hay líneas con un centro de otra rama'}</div>
        <Descargas ruta="dimension" qs={consulta} base="resultados-por-centro" />
      </div>
      <Matriz datos={datos} onColumna={(c) => setPadre({ id: c.id, nombre: c.nombre })} />
    </div>
  );
}

const VACIO = { nombre: '', reporte: 'resultados', frecuencia: 'mensual', dia: 5, hora: 7, formato: 'pdf', destinatarios: '', activo: true };
function Programados() {
  const [lista, setLista] = useState(null);
  const [f, setF] = useState(null);
  const [sinPermiso, setSinPermiso] = useState(false);
  const cargar = useCallback(() => api.get('/contabilidad/reportes-programados').then(r => setLista(r.data.data)).catch(e => { if (e.response?.status === 403) setSinPermiso(true); setLista([]); }), []);
  useEffect(() => { cargar(); }, [cargar]);
  if (sinPermiso) return <div className="bg-white rounded-xl border p-6 text-sm text-gray-500">No tiene permiso para programar el envío de reportes.</div>;
  const guardar = async () => {
    try {
      if (f.id) await api.put(`/contabilidad/reportes-programados/${f.id}`, f); else await api.post('/contabilidad/reportes-programados', f);
      toast.success('Envío guardado'); setF(null); cargar();
    } catch { /* aviso */ }
  };
  const probar = async (p) => {
    try { const r = await api.post(`/contabilidad/reportes-programados/${p.id}/enviar`); r.data.data.ok ? toast.success(r.data.data.mensaje) : toast.error(r.data.data.mensaje); cargar(); } catch { /* aviso */ }
  };
  const DIAS = ['', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
  return (
    <div className="space-y-3" data-prueba="programados">
      <div className="flex items-center"><p className="text-sm text-gray-500 mr-auto">El mensual manda el último período terminado; el semanal, el período en curso hasta ayer.</p>{!f && <button onClick={() => setF({ ...VACIO })} className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-indigo-500 text-indigo-600 rounded-lg text-sm font-medium"><Plus size={14} /> Nuevo envío</button>}</div>
      {f && (
        <div className="bg-white rounded-xl border p-4 flex flex-wrap items-end gap-3 text-sm" data-prueba="form-programado">
          <label className="text-xs text-gray-600">Nombre<input name="nombre" value={f.nombre || ''} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Gerencia" className="block border rounded-lg px-2 py-1.5 mt-1" /></label>
          <label className="text-xs text-gray-600">Reporte<select name="reporte" value={f.reporte} onChange={(e) => setF({ ...f, reporte: e.target.value })} className="block border rounded-lg px-2 py-1.5 mt-1">{TABS.slice(0, 3).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          <label className="text-xs text-gray-600">Cada<select name="frecuencia" value={f.frecuencia} onChange={(e) => setF({ ...f, frecuencia: e.target.value, dia: e.target.value === 'semanal' ? 1 : 5 })} className="block border rounded-lg px-2 py-1.5 mt-1"><option value="mensual">Mes</option><option value="semanal">Semana</option></select></label>
          <label className="text-xs text-gray-600">{f.frecuencia === 'mensual' ? 'Día del mes' : 'Día'}{f.frecuencia === 'mensual'
            ? <input type="number" min="1" max="28" name="dia" value={f.dia} onChange={(e) => setF({ ...f, dia: Number(e.target.value) })} className="block w-20 border rounded-lg px-2 py-1.5 mt-1" />
            : <select name="dia" value={f.dia} onChange={(e) => setF({ ...f, dia: Number(e.target.value) })} className="block border rounded-lg px-2 py-1.5 mt-1">{DIAS.slice(1).map((d, i) => <option key={d} value={i + 1}>{d}</option>)}</select>}</label>
          <label className="text-xs text-gray-600">Hora<input type="number" min="0" max="23" name="hora" value={f.hora} onChange={(e) => setF({ ...f, hora: Number(e.target.value) })} className="block w-20 border rounded-lg px-2 py-1.5 mt-1" /></label>
          <label className="text-xs text-gray-600">Formato<select name="formato" value={f.formato} onChange={(e) => setF({ ...f, formato: e.target.value })} className="block border rounded-lg px-2 py-1.5 mt-1"><option value="pdf">PDF</option><option value="xlsx">Excel</option><option value="ambos">PDF y Excel</option></select></label>
          <label className="text-xs text-gray-600 flex-1 min-w-[260px]">Para (separados por coma)<input name="destinatarios" value={f.destinatarios} onChange={(e) => setF({ ...f, destinatarios: e.target.value })} className="block w-full border rounded-lg px-2 py-1.5 mt-1" /></label>
          <button onClick={() => setF(null)} className="px-3 py-1.5 border rounded-lg">Cancelar</button>
          <button onClick={guardar} className="px-3 py-1.5 border-2 border-indigo-500 text-indigo-600 rounded-lg font-medium">Guardar</button>
        </div>
      )}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm" data-prueba="tabla-programados">
          <thead className="text-[10.5px] uppercase text-gray-500 border-b"><tr><th className="text-left px-4 py-2">Envío</th><th className="text-left px-3">Cuándo</th><th className="text-left px-3">Para</th><th className="text-left px-3">Último</th><th /></tr></thead>
          <tbody className="divide-y">
            {(lista || []).map(p => (
              <tr key={p.id}>
                <td className="px-4 py-2"><div className="font-medium text-gray-900">{p.nombre || p.reporteNombre}</div><div className="text-xs text-gray-500">{p.reporteNombre} · {p.formato === 'ambos' ? 'PDF y Excel' : p.formato === 'xlsx' ? 'Excel' : 'PDF'}</div></td>
                <td className="px-3 text-xs">{p.frecuencia === 'mensual' ? `El ${p.dia} de cada mes` : `Cada ${DIAS[p.dia]}`}, desde las {p.hora}:00{!p.activo && <span className="text-gray-400"> · pausado</span>}</td>
                <td className="px-3 text-xs">{p.destinatarios}</td>
                <td className="px-3 text-xs">{p.ultimoEnvio || '—'}{p.ultimoResultado && <div className="text-gray-500">{p.ultimoResultado}</div>}</td>
                <td className="px-3 whitespace-nowrap text-right">
                  <button title="Enviar ahora" onClick={() => probar(p)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"><Send size={15} /></button>
                  <button title="Cambiar" onClick={() => setF({ ...p })} className="p-1 text-gray-500 hover:bg-gray-100 rounded text-xs">Cambiar</button>
                  <button title="Quitar" onClick={async () => { await api.delete(`/contabilidad/reportes-programados/${p.id}`); cargar(); }} className="p-1 text-gray-400 hover:text-red-600 rounded"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {lista && !lista.length && <tr><td colSpan={5} className="py-8 text-center text-gray-400">Sin envíos programados.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
